import React, { useEffect, useMemo, useState } from 'react';
import type { CSSProperties } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, X, QrCode, BookOpen } from 'lucide-react';
import { useOnboarding } from 'context/OnboardingContext';
import { getPathForPage } from 'utils/navigation';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const CARD_WIDTH = 420;

const computeCardPosition = (
  placement: 'center' | 'right' | 'left' | 'bottom',
  rect: SpotlightRect | null,
  viewportWidth: number,
  viewportHeight: number
): CSSProperties => {
  const safeWidth = Math.min(CARD_WIDTH, viewportWidth - 32);

  if (!rect || viewportWidth < 720) {
    return {
      left: '50%',
      transform: 'translateX(-50%)',
      bottom: Math.min(32, viewportHeight * 0.08)
    };
  }

  switch (placement) {
    case 'right': {
      const left = Math.min(rect.left + rect.width + 32, viewportWidth - safeWidth - 24);
      const top = Math.min(Math.max(rect.top + rect.height / 2, 32), viewportHeight - 32);
      return {
        left,
        top,
        transform: 'translateY(-50%)'
      };
    }
    case 'left': {
      const left = Math.max(rect.left - safeWidth - 32, 24);
      const top = Math.min(Math.max(rect.top + rect.height / 2, 32), viewportHeight - 32);
      return {
        left,
        top,
        transform: 'translateY(-50%)'
      };
    }
    case 'bottom': {
      const top = Math.min(rect.top + rect.height + 28, viewportHeight - 240);
      const left = Math.min(
        Math.max(rect.left + rect.width / 2 - safeWidth / 2, 24),
        viewportWidth - safeWidth - 24
      );
      return {
        left,
        top
      };
    }
    case 'center':
    default:
      return {
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)'
      };
  }
};

const computeSpotlightRect = (
  elementRect: DOMRect | null,
  padding: number,
  viewportWidth: number,
  viewportHeight: number
): SpotlightRect | null => {
  if (!elementRect) {
    return null;
  }

  const paddedTop = Math.max(elementRect.top - padding, 16);
  const paddedLeft = Math.max(elementRect.left - padding, 16);
  const paddedWidth = Math.min(elementRect.width + padding * 2, viewportWidth - paddedLeft - 16);
  const paddedHeight = Math.min(elementRect.height + padding * 2, viewportHeight - paddedTop - 16);

  return {
    top: paddedTop,
    left: paddedLeft,
    width: paddedWidth,
    height: paddedHeight
  };
};

export const OnboardingOverlay: React.FC = () => {
  const navigate = useNavigate();
  const {
    isOpen,
    currentStep,
    currentStepIndex,
    steps,
    goToNextStep,
    goToPreviousStep,
    skipOnboarding,
    closeOnboarding,
    getElementForId,
    onFetchFrom1177
  } = useOnboarding();

  const targetElement = useMemo(() => {
    if (!isOpen || !currentStep.targetId) {
      return null;
    }
    return getElementForId(currentStep.targetId);
  }, [currentStep.targetId, getElementForId, isOpen]);

  const [elementRect, setElementRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isOpen) {
      setElementRect(null);
      return;
    }

    if (!targetElement) {
      setElementRect(null);
      return;
    }

    if (typeof window === 'undefined') {
      return;
    }

    const updateRect = () => {
      setElementRect(targetElement.getBoundingClientRect());
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    window.addEventListener('scroll', updateRect, true);

    const raf = window.requestAnimationFrame(() => {
      targetElement.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
    });

    return () => {
      window.removeEventListener('resize', updateRect);
      window.removeEventListener('scroll', updateRect, true);
      window.cancelAnimationFrame(raf);
    };
  }, [currentStepIndex, isOpen, targetElement]);

  useEffect(() => {
    if (!isOpen || typeof document === 'undefined') {
      return;
    }
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || typeof document === 'undefined') {
    return null;
  }

  const viewportWidth = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const viewportHeight = typeof window !== 'undefined' ? window.innerHeight : 900;
  const padding = currentStep.highlightPadding ?? 18;
  const spotlightRect = computeSpotlightRect(elementRect, padding, viewportWidth, viewportHeight);
  const cardStyle = computeCardPosition(
    currentStep.placement ?? 'center',
    spotlightRect,
    viewportWidth,
    viewportHeight
  );

  const progress = (currentStepIndex + 1) / steps.length;
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;

  const portalTarget = document.body;

  const content = (
    <div className="fixed inset-0 z-modal pointer-events-none font-sans">
      {/* Dimmer */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_20%,rgba(99,102,241,0.15)_0%,rgba(15,23,42,0.82)_65%)] backdrop-blur-[3px] pointer-events-auto" />

      {/* Spotlight */}
      {spotlightRect && (
        <>
          <div
            className="fixed rounded-xl shadow-lg border border-primary/55 pointer-events-none transition-all duration-[350ms] ease-out"
            style={{
              top: spotlightRect.top,
              left: spotlightRect.left,
              width: spotlightRect.width,
              height: spotlightRect.height
            }}
          />
          <div
            className="fixed rounded-xl border-white/25 pointer-events-none animate-pulse transition-all duration-[350ms] ease-out"
            style={{
              top: spotlightRect.top - 6,
              left: spotlightRect.left - 6,
              width: spotlightRect.width + 12,
              height: spotlightRect.height + 12
            }}
          />
        </>
      )}

      {/* Skip button */}
      <button
        type="button"
        className="pointer-events-auto fixed top-7 right-9 max-sm:top-4 max-sm:right-4 px-3 py-2 max-sm:px-2.5 max-sm:py-1.5 rounded-full bg-background-soft0/18 border-primary/35 text-white text-xs max-sm:text-[0.75rem] font-medium uppercase tracking-wider inline-flex items-center gap-1.5 cursor-pointer transition-all hover:bg-background-soft0/30 hover:text-white"
        onClick={skipOnboarding}
        aria-label="Skip onboarding tour"
      >
        <X size={16} />
        Skip tour
      </button>

      {/* Card */}
      <div
        className="fixed bg-card text-foreground-strong p-6 rounded-xl w-[min(420px,calc(100%-2rem))] shadow-lg pointer-events-auto flex flex-col gap-5 border-slate-400/40 max-sm:rounded-xl max-sm:gap-4"
        style={cardStyle}
        role="dialog"
        aria-modal="true"
        aria-label={currentStep.title}
      >
        <div>
          <span className="text-xs uppercase tracking-[0.08em] text-primary font-semibold">
            Step {currentStepIndex + 1} of {steps.length}
          </span>
        </div>

        {/* Progress bar */}
        <div className="h-1.5 bg-slate-400/25 rounded-full overflow-hidden">
          <div
            className="h-full bg-primary rounded-full transition-all duration-[350ms] ease-out"
            style={{ width: `${progress * 100}%` }}
          />
        </div>

        {/* Step header */}
        <div className="flex items-center gap-4">
          <div className="w-[52px] h-[52px] rounded-xl inline-flex items-center justify-center text-white bg-primary shadow-lg">
            {currentStep.icon}
          </div>
          <div className="flex flex-col gap-1">
            <h3 className="text-lg font-semibold m-0">
              {currentStep.title}
            </h3>
            <span className="text-sm font-medium text-foreground-muted">
              {currentStepIndex + 1} / {steps.length} · Discover the essentials
            </span>
          </div>
        </div>

        {/* Body */}
        <div className="flex flex-col gap-3 text-foreground text-sm leading-relaxed [&>p]:m-0 [&>ul]:m-0 [&>ul]:pl-4 [&>ul]:grid [&>ul]:gap-2 [&_li]:list-disc">
          {currentStep.description}
        </div>

        {/* Actions */}
        <div className="flex justify-between items-center gap-4 flex-wrap">
          {isLastStep ? (
            <div className="flex flex-col gap-3 w-full">
              <button
                type="button"
                className="w-full bg-primary border-none text-white rounded-xl py-3 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2.5 cursor-pointer shadow-lg transition-all hover:shadow-lg"
                onClick={() => {
                  skipOnboarding();
                  if (onFetchFrom1177) {
                    onFetchFrom1177();
                  }
                }}
              >
                <QrCode size={18} />
                Fetch your health data from 1177
              </button>
              <button
                type="button"
                className="w-full bg-primary border-none text-white rounded-xl py-3 px-5 text-sm font-semibold inline-flex items-center justify-center gap-2.5 cursor-pointer shadow-lg transition-all hover:shadow-lg"
                onClick={() => {
                  navigate(getPathForPage('health-programs', 'cbt-understanding-eir-space'));
                  closeOnboarding();
                }}
              >
                <BookOpen size={18} />
                Learn How EIR.Space Works
              </button>
            </div>
          ) : (
            <div className="flex gap-3">
              <button
                type="button"
                className="bg-card/25 border-primary/35 text-primary rounded-xl px-4 py-2.5 text-sm font-medium inline-flex items-center gap-2 cursor-pointer transition-all disabled:opacity-45 disabled:cursor-not-allowed hover:enabled:bg-background-soft0/12"
                onClick={goToPreviousStep}
                disabled={isFirstStep}
              >
                <ArrowLeft size={18} />
                Back
              </button>
              <button
                type="button"
                className="bg-primary border-none text-white rounded-xl px-5 py-3 text-sm font-semibold inline-flex items-center gap-2.5 cursor-pointer shadow-lg transition-all hover:shadow-lg"
                onClick={goToNextStep}
              >
                Next step
                <ArrowRight size={18} />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  return ReactDOM.createPortal(content, portalTarget);
};
